/*
 * Target Archery – global behaviour
 * - Money formatting
 * - Add-to-bag helper that opens Dawn's cart drawer
 * - Quick-add popup (product options as dropdowns)
 */
(() => {
  const cfg = window.taConfig || {};
  const strings = window.taStrings || {};
  const TA = (window.TA = window.TA || {});

  /* ---------- Money ---------- */

  TA.formatMoney = (cents, format = cfg.moneyFormat || '€{{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const fmt = (value, decimals, thousands = ',', decimal = '.') => {
      const [int, dec] = (value / 100).toFixed(decimals).split('.');
      const intStr = int.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return dec ? intStr + decimal + dec : intStr;
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = fmt(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = fmt(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = fmt(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = fmt(cents, 2, "'", '.');
        break;
      default:
        value = fmt(cents, 2);
    }
    // Like Liquid's money_without_trailing_zeros: €689 instead of €689.00
    value = value.replace(/[.,]00$/, '');
    return format.replace(match[0], value);
  };

  /* ---------- Cart ---------- */

  TA.addToCart = (items, { button } = {}) => {
    const drawer = document.querySelector('cart-drawer');
    const body = { items };
    if (drawer && drawer.getSectionsToRender) {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    if (button) button.classList.add('is-loading');
    return fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message);
        // PUB_SUB_EVENTS is a top-level const in constants.js – not a window property
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ta', cartData: response });
        }
        if (drawer && drawer.renderContents) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(response);
        } else {
          window.location = window.routes.cart_url;
        }
        return response;
      })
      .finally(() => {
        if (button) button.classList.remove('is-loading');
      });
  };

  /* ---------- Quick add ---------- */

  TA.optionPrompt = (name) => (cfg.prompts || {})[String(name).trim().toLowerCase()] || '';

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const caret =
    '<svg class="ta-select__caret" width="12" height="12" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"/></svg>';

  class QuickAdd {
    constructor() {
      this.el = document.createElement('div');
      this.el.className = 'ta-qa';
      this.el.hidden = true;
      this.el.innerHTML = `
        <div class="ta-qa__overlay" data-ta-qa-close></div>
        <div class="ta-qa__dialog" role="dialog" aria-modal="true" aria-labelledby="TaQaTitle" tabindex="-1"></div>`;
      document.body.appendChild(this.el);
      this.dialog = this.el.querySelector('.ta-qa__dialog');
      this.el.addEventListener('click', (e) => {
        if (e.target.closest('[data-ta-qa-close]')) this.close();
        const add = e.target.closest('[data-ta-qa-add]');
        if (add) this.add(add);
      });
      this.el.addEventListener('change', (e) => {
        const select = e.target.closest('[data-ta-qa-opt]');
        if (!select) return;
        this.selected[+select.dataset.taQaOpt] = select.value;
        this.render();
        const again = this.dialog.querySelector(`[data-ta-qa-opt="${select.dataset.taQaOpt}"]`);
        if (again) again.focus();
      });
      this.el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open(url, opener) {
      this.opener = opener;
      if (opener) opener.classList.add('is-loading');
      return fetch(`${url.split('?')[0]}.js`)
        .then((r) => r.json())
        .then((product) => {
          this.product = product;
          if (product.variants.length === 1 && /^default title$/i.test(product.variants[0].title)) {
            return TA.addToCart([{ id: product.variants[0].id, quantity: 1 }]);
          }
          const first = product.variants.find((v) => v.available) || product.variants[0];
          this.selected = [...first.options];
          this.render();
          this.el.hidden = false;
          requestAnimationFrame(() => this.el.classList.add('is-open'));
          document.body.classList.add('overflow-hidden');
          if (typeof trapFocus === 'function') trapFocus(this.dialog, this.dialog);
          else this.dialog.focus();
        })
        .catch((e) => console.error(e))
        .finally(() => opener && opener.classList.remove('is-loading'));
    }

    close() {
      this.el.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      setTimeout(() => (this.el.hidden = true), 250);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
    }

    variant() {
      return this.product.variants.find((v) => v.options.every((o, i) => o === this.selected[i]));
    }

    render() {
      const p = this.product;
      const v = this.variant();
      const img = (v && v.featured_image && v.featured_image.src) || p.featured_image;
      const price = TA.formatMoney(v ? v.price : p.price);
      const options = p.options
        .map((opt, i) => {
          const name = typeof opt === 'string' ? opt : opt.name;
          const values = typeof opt === 'string' ? [...new Set(p.variants.map((x) => x.options[i]))] : opt.values;
          const prompt = TA.optionPrompt(name);
          // Values not available together with the options chosen before this one
          const avail = (val) =>
            p.variants.some((x) => x.available && x.options[i] === val && x.options.every((o, j) => j >= i || o === this.selected[j]));
          const opts = values
            .map(
              (val) =>
                `<option value="${esc(val)}"${this.selected[i] === val ? ' selected' : ''}>${esc(val)}${
                  avail(val) ? '' : ` – ${esc(strings.soldOut || 'Sold out')}`
                }</option>`
            )
            .join('');
          return `<label class="ta-qa__opt">
              <span class="ta-qa__opt-title">${esc(name)}.${prompt ? `<span class="ta-grey"> ${esc(prompt)}</span>` : ''}</span>
              <span class="ta-select"><select data-ta-qa-opt="${i}">${opts}</select>${caret}</span>
            </label>`;
        })
        .join('');
      const canBuy = v && v.available;
      this.dialog.innerHTML = `
        <div class="ta-qa__head">
          <span class="ta-thumb ta-thumb--88">${img ? `<img src="${img}${img.includes('?') ? '&' : '?'}width=240" alt="">` : ''}</span>
          <div class="ta-qa__meta">
            <span class="ta-meta">${esc(p.vendor)}</span>
            <span class="ta-qa__name" id="TaQaTitle">${esc(p.title)}</span>
            <span class="ta-qa__price">${price}</span>
          </div>
          <button type="button" class="ta-close ta-close--32" data-ta-qa-close aria-label="${esc(strings.close || 'Close')}">
            <svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>
          </button>
        </div>
        ${options}
        <div class="ta-qa__foot">
          <button type="button" class="ta-btn ta-btn--primary ta-btn--block ta-btn--50" data-ta-qa-add ${canBuy ? '' : 'disabled'}>
            ${canBuy ? `${esc(strings.addToBag || 'Add to bag')} · ${price}` : esc(v ? strings.soldOut || 'Sold out' : strings.unavailable || 'Unavailable')}
          </button>
          <a href="${p.url || '#'}${v ? `?variant=${v.id}` : ''}" class="ta-textlink">${esc(strings.viewDetails || 'View full details')}
            <svg width="12" height="12" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"/></svg>
          </a>
          <p class="ta-qa__error" role="alert" hidden></p>
        </div>`;
    }

    add(button) {
      const v = this.variant();
      if (!v || !v.available) return;
      TA.addToCart([{ id: v.id, quantity: 1 }], { button })
        .then(() => this.close())
        .catch((err) => {
          const box = this.dialog.querySelector('.ta-qa__error');
          box.textContent = err.message || strings.cartError || 'Something went wrong.';
          box.hidden = false;
        });
    }
  }

  TA.quickAdd = (url, opener) => {
    TA._qa = TA._qa || new QuickAdd();
    return TA._qa.open(url, opener);
  };

  document.addEventListener('click', (e) => {
    const qa = e.target.closest('[data-ta-quick-add]');
    if (!qa) return;
    e.preventDefault();
    e.stopPropagation();
    TA.quickAdd(qa.dataset.taQuickAdd, qa);
  });
})();
